'use client';
import { useEffect, useRef, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import type { FloraAIChat } from '../../types/flora-ai.types';
import { FloraConversationsPanel } from '../FloraConversationsPanel/FloraConversationsPanel';
import styles from './FloraNavbarTitle.module.css';

interface FloraNavbarTitleProps {
	dossierCount: number;
	chats: FloraAIChat[];
	activeChatId?: string;
	onNewConversation?: () => void;
	onSelectChat?: (chat: FloraAIChat) => void;
}

export const FloraNavbarTitle = ({
	dossierCount,
	chats,
	activeChatId,
	onNewConversation,
	onSelectChat,
}: FloraNavbarTitleProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			const inTrigger = triggerRef.current?.contains(target);
			const inPanel = panelRef.current?.contains(target);
			if (!inTrigger && !inPanel) setIsOpen(false);
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsOpen(false);
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const updatePosition = () => {
			if (!triggerRef.current) return;
			const rect = triggerRef.current.getBoundingClientRect();
			const margin = 16;
			const panelWidth = Math.min(28 * 16, window.innerWidth - margin * 2);
			const maxLeft = window.innerWidth - panelWidth - margin;
			const left = Math.min(Math.max(rect.left, margin), Math.max(maxLeft, margin));
			setPosition({ top: rect.bottom + window.scrollY, left: left + window.scrollX });
		};

		updatePosition();

		const closeOnScroll = (e: Event) => {
			if (panelRef.current?.contains(e.target as Node)) return;
			setIsOpen(false);
		};
		window.addEventListener('scroll', closeOnScroll, true);
		window.addEventListener('resize', updatePosition);
		return () => {
			window.removeEventListener('scroll', closeOnScroll, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen]);

	const handleSelectChat = (chat: FloraAIChat) => {
		onSelectChat?.(chat);
		setIsOpen(false);
	};

	const handleNewConversation = () => {
		onNewConversation?.();
		setIsOpen(false);
	};

	return (
		<div className={styles.row}>
			<h1 className={styles.title}>Nova conversa</h1>
			<button
				ref={triggerRef}
				type="button"
				className={styles.trigger}
				aria-label="Documentos no dossiê"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<span className={styles.badge}>{dossierCount} no dossiê</span>
				<KeyboardArrowDown className={styles.badgeIcon} />
			</button>

			<FloraConversationsPanel
				panelRef={panelRef}
				isOpen={isOpen}
				position={position}
				chats={chats}
				activeChatId={activeChatId}
				onClose={() => setIsOpen(false)}
				onSelectChat={handleSelectChat}
				onNewConversation={handleNewConversation}
			/>
		</div>
	);
};
